import {
	type FC,
	type HTMLAttributes,
	type KeyboardEvent,
	type ReactNode,
	type Ref,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib/utils";
import { Button, type ButtonColor, ClickableIcon, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";

const FOCUSABLE_SELECTOR =
	'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isFocusable = (element: Element | null): element is HTMLElement =>
	element instanceof HTMLElement && element.isConnected && !element.closest("[inert]");

export type PanelSheetStatusTone = "default" | "danger";

export type PanelSheetProps = Omit<HTMLAttributes<HTMLDivElement>, "title" | "children"> & {
	ref?: Ref<HTMLDivElement>;
	/** Header title. Also names the dialog. */
	title: string;
	/** Form fields. Scrolls when taller than the panel and holds to a 600px column. */
	children: ReactNode;
	/** Footer status line: what is missing, or what saving will do. */
	status?: ReactNode;
	/** `danger` turns the status line red, for a required value that is missing. @default "default" */
	statusTone?: PanelSheetStatusTone;
	/** Fires on the backdrop, the close ×, Cancel and Escape. */
	onCancel: () => void;
	/**
	 * The sheet holds unsaved edits. A backdrop click then keeps the sheet open (focus returns to
	 * it), so a stray click cannot throw the edits away; the close ×, Cancel and Escape still cancel.
	 */
	dirty?: boolean;
	/** @default "Cancel" */
	cancelLabel?: string;
	primaryLabel: string;
	onPrimary: () => void;
	/** Button color of the primary action. @default "primary" */
	primaryColor?: ButtonColor;
	/** Disable until the form is valid. */
	primaryDisabled?: boolean;
	/** Show a spinner on the primary action while saving. */
	primaryLoading?: boolean;
	trackingId?: string;
	testId?: string;
};

/**
 * A top-drop sheet for create and edit flows in a docked panel. It fills the nearest `relative`
 * ancestor: a dim backdrop (click to cancel, unless `dirty`) and a white sheet hanging from the top with a
 * header, a scrolling body, and a footer carrying a status line, Cancel and one primary action.
 * Escape cancels unless an inner control (an open dropdown) already used the key.
 *
 * It owns no form state or validation: the feature passes the status copy and `primaryDisabled`.
 */
export const PanelSheet: FC<PanelSheetProps> = ({
	title,
	children,
	status,
	statusTone = "default",
	onCancel,
	dirty = false,
	cancelLabel = "Cancel",
	primaryLabel,
	onPrimary,
	primaryColor = "primary",
	primaryDisabled,
	primaryLoading,
	className,
	trackingId,
	testId = "panel-sheet",
	ref,
	onKeyDown,
	...rest
}) => {
	const titleId = useId();
	const sheetRef = useRef<HTMLDivElement>(null);
	const [opener] = useState(() => document.activeElement);

	useEffect(() => {
		const sheet = sheetRef.current;
		if (sheet && !sheet.contains(document.activeElement)) sheet.focus();
	}, []);

	useEffect(() => {
		const layer = sheetRef.current?.parentElement;
		const container = layer?.parentElement;
		if (!layer || !container) return;
		const covered = Array.from(container.children).filter(
			(child): child is HTMLElement =>
				child instanceof HTMLElement &&
				child !== layer &&
				!child.hasAttribute("inert") &&
				!child.matches("[aria-live]"),
		);
		for (const child of covered) child.setAttribute("inert", "");

		return () => {
			for (const child of covered) child.removeAttribute("inert");
			if (document.activeElement && document.activeElement !== document.body) return;
			if (isFocusable(opener)) {
				opener.focus();
				return;
			}
			const fallback = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).find(isFocusable);
			fallback?.focus();
		};
	}, [opener]);

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		onKeyDown?.(event);
		if (event.key === "Escape" && !event.defaultPrevented) {
			event.preventDefault();
			onCancel();
		}
	};

	return (
		<div
			ref={ref}
			className={cn("absolute inset-0 z-30 flex flex-col", className)}
			data-testid={testId}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<div
				aria-hidden
				className="absolute inset-0 bg-[rgba(15,23,42,0.18)]"
				data-testid={`${testId}-backdrop`}
				onClick={() => {
					if (dirty) sheetRef.current?.focus();
					else onCancel();
				}}
			/>
			<div
				ref={sheetRef}
				role="dialog"
				aria-labelledby={titleId}
				tabIndex={-1}
				data-testid={`${testId}-dialog`}
				className="relative flex max-h-full min-h-0 animate-panel-sheet-drop flex-col border-b border-bc-default bg-surface-base shadow-panel-sheet outline-none"
				onKeyDown={handleKeyDown}
			>
				<div className="flex shrink-0 items-center justify-between gap-3 px-4 pb-1 pt-3">
					<Text id={titleId} type="title" color="primary" className="min-w-0" truncate>
						{title}
					</Text>
					<ClickableIcon
						type="x-mark"
						size="mini"
						color="tertiary"
						aria-label="Close"
						onClick={onCancel}
						testId={`${testId}-close`}
					/>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3.5 pt-2.5 scrollbar-slim">
					<div className="flex max-w-[600px] flex-col gap-3.5">{children}</div>
				</div>
				<div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-bc-subtle px-4 py-2.5">
					<Text
						type="caption"
						color={statusTone === "danger" ? undefined : "tertiary"}
						className={cn("min-w-0 flex-1", statusTone === "danger" && "text-intent-danger-text")}
						role={statusTone === "danger" ? "alert" : "status"}
						data-testid={`${testId}-status`}
						data-tone={statusTone}
					>
						{status}
					</Text>
					<div className="flex shrink-0 items-center gap-2">
						<Button variant="ghost" size="sm" className="text-secondary" onClick={onCancel}>
							{cancelLabel}
						</Button>
						<Button
							color={primaryColor}
							size="sm"
							disabled={primaryDisabled}
							loading={primaryLoading}
							onClick={onPrimary}
						>
							{primaryLabel}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
PanelSheet.displayName = "PanelSheet";
